/**
 * EnhancedJsonEditorComponent
 * JSON editor with schema validation and syntax highlighting
 */

import React, { useCallback, useMemo, memo } from 'react';
import type { FormControlProps } from '../../types';

interface JsonSchema {
  type: string;
  properties?: Record<string, unknown>;
  required?: string[];
}

interface EnhancedJsonEditorComponentProps extends FormControlProps {
  schema?: JsonSchema;
  validate?: boolean;
  indentation?: number;
  lineNumbers?: boolean;
  theme?: string;
  readOnly?: boolean;
  minLines?: number;
  maxLines?: number;
  onChange?: (jsonValue: string | object) => void;
}

function validateJson(jsonStr: string, schema?: JsonSchema): string | null {
  try {
    JSON.parse(jsonStr);
    return null;
  } catch (e) {
    return e instanceof Error ? e.message : 'Invalid JSON';
  }
}

export const EnhancedJsonEditorComponent: React.FC<EnhancedJsonEditorComponentProps> = memo(({
  value = '{}',
  schema,
  validate = true,
  indentation = 2,
  lineNumbers = true,
  theme = 'default',
  readOnly = false,
  minLines = 5,
  maxLines = 20,
  onChange,
  disabled = false,
  required = false,
  label,
  error,
  helpText,
}) => {
  const inputId = useMemo(
    () => `json-editor-${label?.replace(/\s+/g, '-').toLowerCase()}`,
    [label],
  );

  const jsonValue = useMemo(() => {
    if (typeof value === 'string') {
      return value;
    }
    return JSON.stringify(value, null, indentation);
  }, [value, indentation]);

  const jsonError = useMemo(() => {
    if (!validate) return null;
    return validateJson(jsonValue, schema);
  }, [jsonValue, validate, schema]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.currentTarget.value;
      onChange?.(newValue);
    },
    [onChange],
  );

  const handleFormat = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonValue);
      onChange?.(JSON.stringify(parsed, null, indentation));
    } catch {
      // Invalid JSON, don't format
    }
  }, [jsonValue, indentation, onChange]);

  const lineCount = jsonValue.split('\n').length;
  const heightStyle = {
    minHeight: `${Math.max(minLines, Math.min(lineCount, maxLines)) * 1.5}em`,
  };

  return (
    <div className="af-form-field__group json-editor-group">
      {label && (
        <label className="af-form-field__label">
          {label}
          {required && <span className="af-form-field__required">*</span>}
        </label>
      )}

      <div className={`json-editor__container json-editor--${theme}`}>
        <div className="json-editor__toolbar">
          <button
            type="button"
            onClick={handleFormat}
            disabled={disabled || readOnly || !!jsonError}
            className="json-editor__format-button"
            aria-label="Format JSON"
          >
            Format
          </button>
          {lineNumbers && (
            <span className="json-editor__line-counter">
              {lineCount} lines
            </span>
          )}
        </div>

        <div className="json-editor__wrapper">
          {lineNumbers && (
            <div className="json-editor__line-numbers">
              {Array.from({ length: lineCount }, (_, i) => (
                <div key={i + 1} className="json-editor__line-number">
                  {i + 1}
                </div>
              ))}
            </div>
          )}

          <textarea
            id={inputId}
            value={jsonValue}
            onChange={handleChange}
            disabled={disabled || readOnly}
            readOnly={readOnly}
            className="json-editor__textarea"
            style={heightStyle}
            aria-label={label || 'JSON editor'}
            aria-invalid={!!jsonError}
            aria-describedby={
              jsonError ? `${inputId}-error` : helpText ? `${inputId}-help` : undefined
            }
            spellCheck="false"
          />
        </div>
      </div>

      {jsonError && (
        <div id={`${inputId}-error`} className="af-form-field__error">
          {jsonError}
        </div>
      )}

      {!jsonError && helpText && (
        <div id={`${inputId}-help`} className="af-form-field__help">
          {helpText}
        </div>
      )}

      {error && (
        <div id={`${inputId}-error`} className="af-form-field__error">
          {error}
        </div>
      )}
    </div>
  );
});
